import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    DialogBackdrop,
} from '@headlessui/react';
import {
    History,
    RotateCcw,
    Download,
    X,
    BadgeCheck,
} from 'lucide-react';
import moment from 'moment';
import useExperienceVersions, {
    ExperienceVersionMeta,
} from './hooks/useExperienceVersions';

interface ExperienceVersionsPanelProps {
    activationId: number;
    isOpen: boolean;
    onClose: () => void;
    /** Called after a successful restore so the builder reloads the definition. */
    onRestored: () => void;
}

function sourceLabel(source: string): string {
    if (source === 'manual') return 'Manual save';
    if (source === 'backfill') return 'Pre-versioning';
    if (source === 'template') return 'Template';
    if (source.startsWith('template:')) {
        return `Template #${source.split(':')[1]}`;
    }
    if (source.startsWith('import:')) {
        return `Imported from activation ${source.split(':')[1]}`;
    }
    if (source.startsWith('restore:')) {
        return `Restore of v${source.split(':')[1]}`;
    }
    return source;
}

/**
 * Version history for an activation's experience: view metadata, restore a
 * previous version (appends a new version — nothing is lost), or export any
 * version as JSON.
 */
export default function ExperienceVersionsPanel({
    activationId,
    isOpen,
    onClose,
    onRestored,
}: ExperienceVersionsPanelProps) {
    const {
        versions,
        currentVersion,
        isLoading,
        refresh,
        restoreVersion,
        exportVersion,
    } = useExperienceVersions(activationId);

    const [confirmRestore, setConfirmRestore] =
        useState<ExperienceVersionMeta | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (isOpen) {
            refresh();
        }
    }, [isOpen, refresh]);

    const handleRestore = async () => {
        if (!confirmRestore) return;
        setBusy(true);
        const result = await restoreVersion(confirmRestore.versionNumber);
        setBusy(false);
        setConfirmRestore(null);
        if (result) {
            await refresh();
            onRestored();
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            <DialogBackdrop className="fixed inset-0 bg-black/30" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <DialogPanel className="w-full max-w-2xl bg-white rounded-lg shadow-xl max-h-[80vh] flex flex-col">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                        <DialogTitle className="flex items-center space-x-2 text-lg font-semibold text-gray-900">
                            <History className="w-5 h-5" />
                            <span>Version History</span>
                        </DialogTitle>
                        <button
                            onClick={onClose}
                            className="p-1 text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4">
                        {isLoading ? (
                            <p className="text-sm text-gray-500 text-center py-8">
                                Loading versions…
                            </p>
                        ) : versions.length === 0 ? (
                            <p className="text-sm text-gray-500 text-center py-8">
                                No version history yet. Versions are recorded
                                automatically on every save.
                            </p>
                        ) : (
                            <ul className="divide-y divide-gray-200">
                                {versions.map((version) => {
                                    const isCurrent =
                                        version.versionNumber ===
                                        currentVersion;
                                    return (
                                        <li
                                            key={version.id}
                                            className="py-3 flex items-center justify-between"
                                        >
                                            <div>
                                                <div className="flex items-center space-x-2">
                                                    <span className="text-sm font-semibold text-gray-900">
                                                        v
                                                        {version.versionNumber}
                                                    </span>
                                                    {isCurrent && (
                                                        <span className="inline-flex items-center space-x-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                                            <BadgeCheck className="w-3 h-3" />
                                                            <span>Current</span>
                                                        </span>
                                                    )}
                                                    <span className="text-xs text-gray-500">
                                                        {sourceLabel(
                                                            version.source,
                                                        )}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    {moment(
                                                        version.createdAt,
                                                    ).format(
                                                        'MMM D, YYYY h:mm A',
                                                    )}
                                                    {version.createdBy
                                                        ? ` · ${version.createdBy}`
                                                        : ''}
                                                </p>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                {!isCurrent && (
                                                    <button
                                                        onClick={() =>
                                                            setConfirmRestore(
                                                                version,
                                                            )
                                                        }
                                                        className="inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md"
                                                    >
                                                        <RotateCcw className="w-3 h-3" />
                                                        <span>Restore</span>
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() =>
                                                        exportVersion(
                                                            version.versionNumber,
                                                        )
                                                    }
                                                    className="inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-md"
                                                >
                                                    <Download className="w-3 h-3" />
                                                    <span>Export</span>
                                                </button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    {confirmRestore && (
                        <div className="p-4 border-t border-gray-200 bg-yellow-50">
                            <p className="text-sm text-gray-800">
                                Restore <strong>v{confirmRestore.versionNumber}</strong>?
                                The current experience will be preserved in
                                history and the booth will receive this version
                                on its next refresh.
                            </p>
                            <div className="mt-3 flex space-x-2">
                                <button
                                    onClick={handleRestore}
                                    disabled={busy}
                                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                                >
                                    {busy ? 'Restoring…' : 'Restore'}
                                </button>
                                <button
                                    onClick={() => setConfirmRestore(null)}
                                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </DialogPanel>
            </div>
        </Dialog>
    );
}
